'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { Button, Cell } from '@vinted/web-ui'
import { isNil, omitBy } from 'lodash'
import classNames from 'classnames'

import { BundleFooterPricing } from '@marketplace-web/escrow-pricing/escrow-pricing-feature'
import { ResponseError, useFetch } from '@marketplace-web/core-api/api-client-util'
import { useBreakpoint } from '@marketplace-web/breakpoints/breakpoints-feature'
import { navigateToPage, usePreventWindowUnload } from '@marketplace-web/shared/browser-util'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { ProductItemModel } from '@marketplace-web/item-box/product-item-data'
import {
  BundleShippingType,
  getBundleOrderReview,
  editBundleTransaction,
  transformInstantBundleSummaryDto,
  transformBundleTransaction,
  BundleSummaryModel,
  BundleTransactionDto,
  BundleTransactionModel,
  buyerBuyEvent,
  buyerWantItemEvent,
  clickEvent,
  BundlingLimitationsErrorType,
  exceedingLimitBundleClickEvent,
} from '@marketplace-web/bundles/items-bundle-data'
import { CONVERSATION_URL } from '@marketplace-web/messaging/conversation-messaging-data'
import {
  storeCheckoutItemScrollOnLoadMark,
  useNavigateToCheckout,
} from '@marketplace-web/checkout/checkout-feature'
import {
  CheckoutOrderTypeMap,
  ExtraServiceOrderType,
} from '@marketplace-web/checkout/checkout-data'
import { getHomepageSessionIdFromUrl } from '@marketplace-web/home/home-page-util'
import { CurrencyAmountModel } from '@marketplace-web/currency/currency-data'

import { useCreateBundleInquiry } from '@marketplace-web/messaging/conversation-messaging-api-resolvers-feature'

import ItemThumbnails from '../ItemThumbnails'
import InstantBundleSummaryDialog from '../InstantBundleSummaryDialog'
import BundlingLimitationsModal from '../../../components/BundlingLimitationsModal'
import { resolveBundleError } from '../resolveBundleError'

type Props = {
  selectedItems: Array<ProductItemModel>
  transactionId: number | null
  bundleTrackingId: string | null
  bundleSummary: BundleSummaryModel | undefined
  isSellerBusiness: boolean
  homepageSessionId: string | null
  sellerId: number
  isBundleSummaryLoading: boolean
  checkoutId?: string | null
  warnOnUnsavedChanges: boolean
  screen: string
  onReviewBundleLoading: (value: boolean) => void
  escrowNoteTranslationKey?: string
}

type TransactionConfirmationProps = {
  bundleShippingType?: BundleShippingType | null
  redirectToCheckout?: boolean
  openOfferRequestModal?: boolean
}

const BundleFooter = ({
  bundleSummary,
  bundleTrackingId,
  isSellerBusiness,
  selectedItems,
  transactionId,
  homepageSessionId,
  sellerId,
  isBundleSummaryLoading,
  checkoutId,
  warnOnUnsavedChanges,
  screen,
  onReviewBundleLoading,
  escrowNoteTranslationKey,
}: Props) => {
  const { track } = useTracking()
  const translate = useTranslate('bundle_items')
  const breakpoints = useBreakpoint()

  const { createBundleInquiry } = useCreateBundleInquiry()
  const { navigateToCheckout: navigateAndCreateNewCheckout, navigateToExistingCheckout } =
    useNavigateToCheckout()

  const [isBundleErrorShown, setIsBundleErrorShown] = useState<boolean>(false)
  const [bundleLimitationError, setBundleLimitationError] = useState<{
    limit: CurrencyAmountModel | null
    type: BundlingLimitationsErrorType
  } | null>(null)
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [isEditCheckoutTransactionLoading, setIsEditCheckoutTransactionLoading] = useState(false)
  const [bundleTransaction, setBundleTransaction] = useState<BundleTransactionModel | null>(null)
  const [skipNavigationWarning, setSkipNavigationWarning] = useState(false)
  const itemThumbnailsContainerRef = useRef<HTMLDivElement | null>(null)

  const hasUnsavedBundleChanges = selectedItems.length > 0
  const selectedItemIds = selectedItems.map(({ id }) => id)

  const handleCancelBundle = () => {
    track(
      clickEvent({
        screen,
        target: 'cancel_bundle',
        targetDetails: JSON.stringify({
          bundle_id: bundleTrackingId,
        }),
      }),
    )
  }

  usePreventWindowUnload(
    warnOnUnsavedChanges && hasUnsavedBundleChanges && !skipNavigationWarning,
    handleCancelBundle,
  )

  const {
    fetch: fetchOrderReview,
    transformedData: orderReview,
    isLoading: isOrderReviewLoading,
    error: orderReviewError,
  } = useFetch(getBundleOrderReview, response =>
    transformInstantBundleSummaryDto(response.bundle_order_review),
  )

  const handleBundleTransactionResponseSuccess = (
    bundleTransactionResponse: BundleTransactionDto,
  ) => {
    const transformedBundleTransaction = transformBundleTransaction(bundleTransactionResponse)

    setBundleTransaction(transformedBundleTransaction)
  }

  const createTransaction = async ({
    bundleShippingType,
    redirectToCheckout = false,
    openOfferRequestModal = false,
  }: TransactionConfirmationProps) => {
    track(
      clickEvent({
        screen: 'bundling',
        target: 'create_bundle',
        targetDetails: JSON.stringify(
          omitBy(
            {
              bundle_id: bundleTrackingId,
              transaction_id: transactionId,
            },
            isNil,
          ),
        ),
      }),
    )

    selectedItemIds.forEach(id =>
      track(
        buyerWantItemEvent({
          transactionId,
          itemId: id,
          button: 'create_bundle',
          globalSearchSessionId: null,
          searchSessionId: null,
          homepageSessionId,
        }),
      ),
    )

    const response = await createBundleInquiry({
      selectedItemIds,
      bundleTrackingId,
      bundleShippingType,
      receiverId: sellerId,
    })

    if (!response) return

    const { id, conversationId } = response

    if (redirectToCheckout) {
      track(
        buyerBuyEvent({
          transactionId: transactionId || id,
          screen: 'bundle_summary',
          homepageSessionId: getHomepageSessionIdFromUrl(),
        }),
      )

      setSkipNavigationWarning(true)
      navigateAndCreateNewCheckout(id, CheckoutOrderTypeMap[ExtraServiceOrderType.Transaction])

      return
    }

    if (openOfferRequestModal) {
      setBundleTransaction(response)

      return
    }

    setSkipNavigationWarning(true)
    navigateToPage(CONVERSATION_URL(conversationId))
  }

  const editTransaction = async ({
    bundleShippingType,
    redirectToCheckout = false,
    openOfferRequestModal = false,
  }: TransactionConfirmationProps) => {
    if (!transactionId) return

    const response = await editBundleTransaction({
      transactionId,
      selectedItemIds,
      bundleTrackingId,
      bundleShippingType,
    })

    if ('errors' in response) return

    const {
      transaction: { id, user_msg_thread_id },
    } = response

    if (redirectToCheckout) {
      track(
        buyerBuyEvent({
          transactionId: transactionId || id,
          screen: 'bundle_summary',
          homepageSessionId: getHomepageSessionIdFromUrl(),
        }),
      )

      setSkipNavigationWarning(true)
      navigateAndCreateNewCheckout(id, CheckoutOrderTypeMap[ExtraServiceOrderType.Transaction])

      return
    }

    if (openOfferRequestModal) {
      handleBundleTransactionResponseSuccess(response.transaction)

      return
    }

    setSkipNavigationWarning(true)
    navigateToPage(CONVERSATION_URL(user_msg_thread_id))
  }

  const editTransactionOnCheckoutFlow = useCallback(async () => {
    if (!transactionId) return

    const response = await editBundleTransaction({
      transactionId,
      selectedItemIds,
      bundleTrackingId,
      bundleShippingType: orderReview?.bundleShippingType,
    })

    if ('errors' in response) return

    track(
      buyerBuyEvent({
        transactionId: transactionId || response.transaction.id,
        screen: 'bundle_summary',
        homepageSessionId: getHomepageSessionIdFromUrl(),
      }),
    )

    if (!checkoutId) return

    storeCheckoutItemScrollOnLoadMark()

    setSkipNavigationWarning(true)
    navigateToExistingCheckout(
      checkoutId,
      transactionId,
      CheckoutOrderTypeMap[ExtraServiceOrderType.Transaction],
    )
  }, [
    bundleTrackingId,
    checkoutId,
    navigateToExistingCheckout,
    orderReview?.bundleShippingType,
    selectedItemIds,
    track,
    transactionId,
  ])

  const clearBundleLimitationError = () => {
    setBundleLimitationError(null)
  }

  const handleBundleLimitationError = useCallback(
    (type: BundlingLimitationsErrorType, limit: CurrencyAmountModel | null) => {
      if (isBundleErrorShown) return

      setBundleLimitationError({
        limit,
        type,
      })

      setIsBundleErrorShown(true)

      track(
        exceedingLimitBundleClickEvent({
          screen: 'bundling',
          bundleSessionId: bundleTrackingId ?? '',
        }),
      )
    },
    [bundleTrackingId, isBundleErrorShown, track],
  )

  const handleBundleError = useCallback(
    (error: ResponseError) => {
      const resolvedError = resolveBundleError(error)

      if (!resolvedError) return undefined

      if (resolvedError.type === BundlingLimitationsErrorType.BundleExceedsLimit) {
        handleBundleLimitationError(
          BundlingLimitationsErrorType.BundleExceedsLimit,
          resolvedError.limit,
        )
      }

      return undefined
    },
    [handleBundleLimitationError],
  )

  useEffect(() => {
    if (orderReviewError && !isBundleErrorShown) {
      handleBundleError(orderReviewError)
    }
  }, [orderReviewError, handleBundleError, isBundleErrorShown])

  useEffect(() => {
    const performCheckoutFlow = () => {
      if (!checkoutId || !orderReview) return

      if (
        orderReview?.bundleShippingType === BundleShippingType.CustomOrNoShipping ||
        orderReview?.bundleShippingType === BundleShippingType.ShippingUnavailable
      ) {
        setIsModalVisible(true)
      } else {
        if (isEditCheckoutTransactionLoading) return

        setIsEditCheckoutTransactionLoading(true)
        editTransactionOnCheckoutFlow()
      }
    }

    performCheckoutFlow()
  }, [checkoutId, editTransactionOnCheckoutFlow, isEditCheckoutTransactionLoading, orderReview])

  useEffect(() => {
    const performDefaultFlow = () => {
      if (orderReview && !checkoutId) {
        setIsModalVisible(true)
      }
    }

    performDefaultFlow()
  }, [checkoutId, orderReview])

  useEffect(() => {
    const finishReviewBundleLoadingOnModalOpen = () => {
      if (isModalVisible || orderReviewError) {
        onReviewBundleLoading(false)
      }
    }

    finishReviewBundleLoadingOnModalOpen()
  }, [isModalVisible, onReviewBundleLoading, orderReviewError])

  const handleReviewBundleClick = async () => {
    setIsBundleErrorShown(false)
    onReviewBundleLoading(true)

    await fetchOrderReview({ itemIds: selectedItemIds, sellerId })

    track(
      clickEvent({
        screen,
        target: 'create_bundle',
        targetDetails: JSON.stringify({
          bundle_id: bundleTrackingId,
        }),
      }),
    )
  }

  const handleReviewBundleModalClose = () => {
    setIsModalVisible(false)
  }

  const renderActions = () => {
    return (
      <Button
        text={translate('actions.review')}
        disabled={
          !selectedItems.length ||
          isOrderReviewLoading ||
          isEditCheckoutTransactionLoading ||
          isBundleSummaryLoading
        }
        size={Button.Size.Medium}
        styling={Button.Styling.Filled}
        inline
        onClick={handleReviewBundleClick}
        testId="bundle-footer-action-button"
        isLoading={isEditCheckoutTransactionLoading || isOrderReviewLoading}
      />
    )
  }

  const footerClassName = classNames('u-ui-padding-vertical-small', {
    'u-ui-padding-vertical-medium': breakpoints.tabletsUp,
  })

  return (
    <>
      <div className={footerClassName}>
        <Cell
          prefix={
            <BundleFooterPricing
              isSellerBusiness={isSellerBusiness}
              selectedItems={selectedItems}
              bundleSummary={bundleSummary}
              isBundleSummaryLoading={isBundleSummaryLoading}
              escrowNoteTranslationKey={escrowNoteTranslationKey}
            />
          }
          suffix={renderActions()}
          body={
            <div className="u-tablets-up-only" ref={itemThumbnailsContainerRef}>
              <ItemThumbnails
                isDynamic
                items={selectedItems}
                containerRef={itemThumbnailsContainerRef}
              />
            </div>
          }
          styling={Cell.Styling.Tight}
        />
      </div>
      {orderReview && bundleSummary && (
        <InstantBundleSummaryDialog
          show={isModalVisible}
          onClose={handleReviewBundleModalClose}
          bundleSummary={bundleSummary}
          instantBundleSummary={orderReview}
          bundleTrackingId={bundleTrackingId}
          selectedItems={selectedItems}
          onConfirm={transactionId ? editTransaction : createTransaction}
          sellerId={sellerId}
          bundleTransaction={bundleTransaction}
          onSkipNavigationWarning={() => setSkipNavigationWarning(true)}
        />
      )}
      {!!bundleLimitationError && (
        <BundlingLimitationsModal
          isOpen={!!bundleLimitationError}
          limit={bundleLimitationError.limit}
          type={bundleLimitationError.type}
          bundleTrackingId={bundleTrackingId}
          onClose={clearBundleLimitationError}
        />
      )}
    </>
  )
}

export default BundleFooter
